import React, { createContext, useState, useContext, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import io from 'socket.io-client';
import axios from 'axios';
import { SOCKET_URL, API_URL } from '../config';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  // Map of source -> count  (e.g. "channel:general" -> 3, "dm:5" -> 1)
  const [unreadSources, setUnreadSources] = useState({});
  // @mention counts per channel (keys: "mention:channel:<name>")
  const [mentionUnreadSources, setMentionUnreadSources] = useState({});
  const [mentionUnreadCount, setMentionUnreadCount] = useState(0);
  const socketRef = useRef(null);
  const isOnChatPageRef = useRef(location.pathname === '/chat');

  useEffect(() => {
    isOnChatPageRef.current = location.pathname === '/chat';
  }, [location.pathname]);

  // Track which chat the user is currently viewing (set by Chat component)
  const activeChatRef = useRef(null); // { type: 'channel', name: '...' } or { type: 'dm', userId: ... }

  const setActiveChat = useCallback((chat) => {
    activeChatRef.current = chat;
  }, []);

  /** Join a channel room on the notification socket (call when new channels are created). */
  const joinChatRoomForNotifications = useCallback((roomName) => {
    if (!roomName || !socketRef.current) return;
    socketRef.current.emit('join-room', String(roomName));
  }, []);

  const fetchAndJoinAllChannelRooms = useCallback(async (socket) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await axios.get(`${API_URL}/chat/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data.channels || [];
      list.forEach((ch) => {
        if (ch?.name) socket.emit('join-room', String(ch.name));
      });
    } catch (e) {
      console.error('Could not join channel rooms for chat notifications', e);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setUnreadSources({});
      setMentionUnreadSources({});
      setMentionUnreadCount(0);
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
    });

    socket.on('connect', () => {
      socket.emit('join-room', 'general');
      fetchAndJoinAllChannelRooms(socket);
    });

    socket.on('receive-message', (data) => {
      const myId = user?.id != null ? parseInt(String(user.id), 10) : NaN;
      const fromId =
        data.userId != null ? parseInt(String(data.userId), 10) : NaN;
      if (!Number.isNaN(myId) && !Number.isNaN(fromId) && fromId === myId) return;

      // Determine the source key
      let sourceKey = null;

      if (data.recipientId !== undefined && data.recipientId !== null && data.recipientId !== '') {
        const recip = parseInt(String(data.recipientId), 10);
        if (!Number.isNaN(recip) && recip === myId && !Number.isNaN(fromId)) {
          sourceKey = `dm:${fromId}`;
        }
      } else if (data.room) {
        sourceKey = `channel:${data.room}`;
      }

      if (!sourceKey) return;

      // Check if user is currently viewing this exact chat
      const active = activeChatRef.current;
      if (isOnChatPageRef.current && active) {
        if (
          (active.type === 'channel' && sourceKey === `channel:${active.name}`) ||
          (active.type === 'dm' && sourceKey === `dm:${active.userId}`)
        ) {
          return;
        }
      }

      // Increment per-source count
      setUnreadSources((prev) => ({
        ...prev,
        [sourceKey]: (prev[sourceKey] || 0) + 1,
      }));

      // Increment total count
      setUnreadCount((prev) => prev + 1);

      // @mentions in channels (separate badge)
      if (data.room && Array.isArray(data.mentionedUserIds) && data.mentionedUserIds.length > 0) {
        const imMentioned = data.mentionedUserIds.some(
          (id) => parseInt(String(id), 10) === myId
        );
        if (imMentioned) {
          const mk = `mention:channel:${data.room}`;
          setMentionUnreadSources((prev) => ({
            ...prev,
            [mk]: (prev[mk] || 0) + 1,
          }));
          setMentionUnreadCount((c) => c + 1);
        }
      }
    });

    socketRef.current = socket;

    return () => {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }
    };
  }, [user, fetchAndJoinAllChannelRooms]);

  // Clear ALL notifications (used when navigating to chat page)
  const clearNotifications = useCallback(() => {
    setUnreadCount(0);
    setUnreadSources({});
    setMentionUnreadSources({});
    setMentionUnreadCount(0);
  }, []);

  // Clear notifications for a specific source (used when switching channels/DMs)
  const clearSourceNotification = useCallback((sourceKey) => {
    setUnreadSources((prev) => {
      if (!prev[sourceKey]) return prev;
      const next = { ...prev };
      const removed = next[sourceKey] || 0;
      delete next[sourceKey];
      setUnreadCount((c) => Math.max(0, c - removed));
      return next;
    });
    if (sourceKey.startsWith('channel:')) {
      const name = sourceKey.replace(/^channel:/, '');
      const mk = `mention:channel:${name}`;
      setMentionUnreadSources((prev) => {
        if (!prev[mk]) return prev;
        const next = { ...prev };
        const removed = next[mk] || 0;
        delete next[mk];
        setMentionUnreadCount((c) => Math.max(0, c - removed));
        return next;
      });
    }
  }, []);

  // Check if a specific source has unread messages
  const hasUnreadFrom = useCallback(
    (sourceKey) => (unreadSources[sourceKey] || 0) > 0,
    [unreadSources]
  );

  const unreadCountFor = useCallback(
    (sourceKey) => unreadSources[sourceKey] || 0,
    [unreadSources]
  );

  const mentionCountFor = useCallback(
    (channelName) => mentionUnreadSources[`mention:channel:${channelName}`] || 0,
    [mentionUnreadSources]
  );

  // Whether there are ANY unread messages
  const hasUnread = unreadCount > 0;
  const hasMentionUnread = mentionUnreadCount > 0;

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        unreadSources,
        hasUnread,
        hasUnreadFrom,
        unreadCountFor,
        mentionUnreadCount,
        mentionUnreadSources,
        mentionCountFor,
        hasMentionUnread,
        clearNotifications,
        clearSourceNotification,
        setActiveChat,
        joinChatRoomForNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
